<template>
  <nav :aria-label="$t(&quot;breadcrumb&quot;)">
    <ol
      class="px-0 pt-1 pb-0 mb-0 bg-transparent breadcrumb"
      :class="this.$store.state.isRTL ? '' : ' me-sm-6'"
    >
      <li class="text-sm breadcrumb-item" :class="textWhite">
        <a
          v-if="this.$store.state.isRTL"
          :class="textWhite"
          class="opacity-5 ps-2"
          href="#"
          >لوحات القيادة</a
        >
        <a v-else :class="textWhite" class="opacity-8" href="#"><portal-text message="Pages" :help="false" /></a>
      </li>
      <li
        class="text-sm breadcrumb-item active"
        :class="textWhite ? 'text-white' : 'text-dark'"
        aria-current="page"
      >
        <portal-text :message="currentPage" />
      </li>
    </ol>
    <h2><portal-text message="Client: {0} {1}" :values="[clientName, clientLastName]" /></h2>
    <div>
      <button
        v-if="showTimeOutButton"
        class="btn mt-3 btn-warning"
        @click="timeOutEntry"
      >
        <portal-text message="Time Out" :help="false" />
      </button>
      <button
        v-if="!showTimeOutButton"
        class="btn mt-3"
        :class="timeOutButtonClass"
        @click="toggleServiceCodeInput"
      >
        <portal-text :message="timeOutButtonText" :help="false" />
      </button>
      <div v-if="showServiceCodeInput" class="mt-3">
        <label for=""><portal-text message="Service Code" /></label>
        <select
          v-model="serviceCode"
          class="form-control d-inline-block w-auto"
        >
          <option disabled value="">
            {{ serviceCode.length
                ? $t("---Select Service Code---")
                : $t("No service code available") }}
          </option>
          <option v-for="code in serviceCodes" :key="code" :value="code">
            {{ code }}
          </option>
        </select>
        <button class="btn btn-success ml-2 mt-3" @click="submitServiceCode">
          <portal-text message="Submit" :help="false" />
        </button>
      </div>
    </div>
    <div v-if="timeInHours !== null">
      <p><portal-text message="Current Running Time: {0} hours" :values="[timeInHours]" /></p>
    </div>
  </nav>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment-timezone";
import { isPublicAuthPath } from "@/utils/publicAuthRoutes";

// The legacy check_time_in API returns offset-free dates in config/app.php's timezone.
const TIMESHEET_TIMEZONE = "America/Los_Angeles";

export default {
  name: "breadcrumbs",
  props: {
    currentPage: {
      required: true,
    },
    textWhite: {
      type: String,
    },
  },
  data() {
    return {
      showServiceCodeInput: false,
      serviceCode: [],
      timeInHours: null,
      timesheetID: null,
      showTimeOutButton: false,
      timeInterval: null,
      elapsedAtSync: null,
      timerSyncedAt: null,
      timeCheckRequestId: 0,
      timerDisposed: false,
      isMobile: false,
      clientName: null,
      clientLastName: null,
      status: {
        personalInformation: false,
        paymentDetails: false,
        serviceDetails: false,
        eligibility: false,
        signature: false,
      },
    };
  },
  computed: {
    timeOutButtonClass() {
      return this.showServiceCodeInput ? "btn-danger" : "btn-info";
    },
    timeOutButtonText() {
      return this.showServiceCodeInput ? "Cancel" : "Time In";
    },
  },
  async mounted() {
    if (isPublicAuthPath(this.$route.path)) return;
    if (!localStorage.getItem("token")) return;

    await this.checkTimeInEntry();
    if (this.timerDisposed) return;
    this.startTimeInterval();
    document.addEventListener("visibilitychange", this.refreshRunningTime);
    this.detectMobile();
    this.fetchClients();
    this.fetch_employee_data();
  },
  beforeUnmount() {
    this.timerDisposed = true;
    clearInterval(this.timeInterval);
    this.timeInterval = null;
    document.removeEventListener("visibilitychange", this.refreshRunningTime);
  },
  methods: {
    fetchClients() {
      const token = localStorage.getItem("token");
      if (!token) return;
      if (isPublicAuthPath(this.$route.path)) return;

      fetch(`${process.env.VUE_APP_BACKEND_DOMAIN}/api/client_fetch_limit_1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then(async (response) => {
          if (!response.ok) return;
          const data = await response.json();
          const clients = (data?.clients || []).filter((entry) => Number(entry.status) === 1 && entry.client);
          const selectedId = localStorage.getItem("selectedClientId");
          const client = (clients.find((entry) => String(entry.client.id) === selectedId) || clients[0])?.client;
          if (!client) return;
          localStorage.setItem("selectedClientId", client.id);
          localStorage.setItem("clientName", client.first_name);
          localStorage.setItem("clientLastName", client.last_name);
          this.clientName = client.first_name;
          this.clientLastName = client.last_name;
        })
        .catch((error) => {
          console.error("Error fetching clients:", error);
        });
    },
    detectMobile() {
      // Detect if the device is mobile (screen width less than 768px)
      this.isMobile = window.innerWidth < 768;
    },
    async checkTimeInEntry() {
      const token = localStorage.getItem("token");
      if (!token || this.timerDisposed) return;
      if (isPublicAuthPath(this.$route.path)) return;

      const requestId = ++this.timeCheckRequestId;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/check_time_in`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            validateStatus: (status) => status < 500,
          }
        );

        if (this.timerDisposed || requestId !== this.timeCheckRequestId || response.status !== 200) return;

        if (response.data?.timesheet) {
          this.showServiceCodeInput = false;
          this.showTimeOutButton = true;
          this.timesheetID = response.data.timesheet.id;
          this.syncRunningTime(response.data.timesheet);
        } else {
          this.resetRunningTime();
        }
      } catch (error) {
        console.error("Error checking time in entry:", error);
      }
    },

    syncRunningTime({ start_date, start_time, server_time }) {
      this.elapsedAtSync = null;
      this.timerSyncedAt = null;
      this.timeInHours = null;
      if (!start_date || !start_time || !server_time) return;

      const format = "YYYY-MM-DD HH:mm:ss";
      const start = moment.tz(`${start_date} ${start_time}`, format, true, TIMESHEET_TIMEZONE);
      const server = moment.tz(server_time, format, true, TIMESHEET_TIMEZONE);
      if (!start.isValid() || !server.isValid()) return;

      this.elapsedAtSync = server.valueOf() - start.valueOf();
      this.timerSyncedAt = performance.now();
      this.calculateTimeInHours();
    },

    calculateTimeInHours() {
      if (this.elapsedAtSync === null || this.timerSyncedAt === null) return;
      // Advance the server snapshot with a monotonic clock, independent of the
      // browser's timezone, wall-clock corrections and delayed interval callbacks.
      const elapsed = this.elapsedAtSync + performance.now() - this.timerSyncedAt;
      const totalSeconds = Math.floor(Math.max(0, elapsed) / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      this.timeInHours = `${this.padTime(hours)}:${this.padTime(
        minutes
      )}:${this.padTime(seconds)}`;
    },

    resetRunningTime() {
      this.showTimeOutButton = false;
      this.showServiceCodeInput = false;
      this.timesheetID = null;
      this.elapsedAtSync = null;
      this.timerSyncedAt = null;
      this.timeInHours = null;
    },

    refreshRunningTime() {
      // Re-anchor after a background tab or a suspended device resumes.
      if (document.visibilityState === "visible") this.checkTimeInEntry();
    },

    padTime(time) {
      return time < 10 ? `0${time}` : time;
    },
    async fetch_employee_data() {
      const token = localStorage.getItem("token");
      if (!token) return;
      if (isPublicAuthPath(this.$route.path)) return;

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-employee-data`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.success) {
          let codesString = res.data.data.service_code || "";
          codesString = codesString.replace(/["[\]]/g, "");
          this.serviceCodes = codesString.split(",").map((code) => code.trim());
          this.serviceCode = this.serviceCodes.length
            ? this.serviceCodes[0]
            : null;
        }
      } catch (err) {
        console.error("Failed to fetch employee data", err);
        Swal.fire("Error", "Could not fetch employee data", "error");
      }
    },
    async fetch_checklist() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/enrollment-checklist`,
          {
            params: { client_id: localStorage.getItem("selectedClientId") },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (res.data.success) {
          this.status.personalInformation = res.data.personal_information;
          this.status.paymentDetails = res.data.payment_details;
          this.status.serviceDetails = res.data.service_details;
          this.status.eligibility = res.data.eligibility;
          this.status.signature = res.data.signature;
          const failed = [];
          if (!this.status.personalInformation)
            failed.push("Personal Information not completed");
          if (!this.status.paymentDetails)
            failed.push("Payment Details missing");
          if (!this.status.serviceDetails)
            failed.push("Service Details not set");
          if (!this.status.eligibility)
            failed.push("Eligibility requirements not met");
          if (!this.status.signature) failed.push("Signature not uploaded");

          if (failed.length > 0) {
            Swal.fire({
              icon: "warning",
              title: "Checklist Incomplete",
              html: `<ul style="text-align:left;">${failed
                .map((msg) => `<li>${this.$message(msg)}</li>`)
                .join("")}</ul>`,
            });
            return false;
          }
          return true;
        }
      } catch (err) {
        console.error("Failed to fetch status", err);
        return false;
      }
    },

    startTimeInterval() {
      if (this.timerDisposed) return;
      clearInterval(this.timeInterval);
      this.timeInterval = setInterval(() => {
        if (this.showTimeOutButton) this.calculateTimeInHours();
      }, 1000);
    },

    async toggleServiceCodeInput() {
      let checklist = await this.fetch_checklist(); // wait for the result
      if (!checklist) return; // will now correctly block if false
      this.showServiceCodeInput = !this.showServiceCodeInput;
    },

    async timeOutEntry() {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("timesheet_Id", this.timesheetID);
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/time_out`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        console.log(response);
        ++this.timeCheckRequestId;
        this.resetRunningTime();
        location.reload();
        this.checkTimeInEntry();
        this.showServiceCodeInput = true;
      } catch (error) {
        console.error("Error submitting service code:", error);
      }
    },

    async submitServiceCode() {
      const client_id = localStorage.getItem("selectedClientId");
      if (!client_id) {
        return Swal.fire({
          icon: "error",
          title: "Oops...",
          text: `Please Select a Client to clock in`,
        });
      }
      if (!this.serviceCode) {
        return Swal.fire({
          icon: "error",
          title: "Oops...",
          text: `Please add your service code in the enrollment menu.`,
        });
      }
      console.log(`Service Code Submitted: ${this.serviceCode}`);
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("service_code", this.serviceCode);
      formData.append("client_id", client_id);
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/time_in`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        console.log(response);
        this.checkTimeInEntry();
        this.showServiceCodeInput = true;
      } catch (error) {
        if (error.response && error.response.status === 400) {
          console.error("Validation errors:", error.response.data.error);
          const errorMessage = error.response.data.error.client_id[0];
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: `${errorMessage}`,
          });
        } else {
          console.error("Error submitting service code:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
.mt-3 {
  margin-top: 1rem;
}
.ml-2 {
  margin-left: 0.5rem;
}
</style>
